import React, { useState } from "react";
import { useFormik } from "formik";
import { embedInitialValues, embedValidationSchema } from "./validationSchema";
import { SubSection } from "context/BlogContext";
import { FileEdit, Save, Trash2 } from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

interface EmbedProps {
  sectionOrder: number;
  subSectionOrder: number;
  handleSaveSubSection: (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => void;
  handleEditSubSection: (sectionOrder: number, subSection: SubSection) => void;
  subSection: SubSection;
  handleDeleteSubSection: (subsectionOrder: number) => void;
}

const Embed: React.FC<EmbedProps> = ({
  sectionOrder,
  subSectionOrder,
  handleSaveSubSection,
  handleEditSubSection,
  subSection,
  handleDeleteSubSection,
}) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isCancel, setIsCancel] = useState(false);

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: embedInitialValues(subSection),
    validationSchema: embedValidationSchema,
    onSubmit: (values) => {
      handleSaveSubSection(sectionOrder, {
        title: values.title,
        type: "embed",
        attrs: { url: values.url, caption: values.caption },
        isEditing: false,
        order: subSectionOrder,
      });
    },
  });

  // Handle delete confirmation
  const handleDelete = () => {
    handleDeleteSubSection(subSection.order);
    setDeleteModal(false);
  };

  const handleCancelSubSection = () => {
    handleSaveSubSection(sectionOrder, {
      ...subSection,
      isEditing: true,
      // isEditing: subSection.isEditing ? false : true,
    });
    validation.resetForm();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  return (
    <div>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        message="Are you sure you want to remove this subsection?"
      />
      {subSection.isEditing ? (
        <form>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12 pt-5">
            <div className="xl:col-span-6">
              <label
                htmlFor={`subTitle`}
                className="inline-block mb-2 text-base font-medium"
              >
                Title
              </label>
              <input
                type="text"
                id="title"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Title"
                name="title"
                onChange={validation.handleChange} // Bind Formik onChange
                value={validation.values.title} // Bind Formik value
              />
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor={`url`}
                className="inline-block mb-2 text-base font-medium"
              >
                Url
              </label>
              <input
                type="text"
                id="url"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Url"
                name="url"
                onChange={validation.handleChange} // Bind Formik onChange
                value={validation.values.url} // Bind Formik value
              />
              {validation.errors.url && validation.touched.url && (
                <p className="mt-1 text-sm text-red-400">
                  {String(validation.errors.url)}
                </p>
              )}
            </div>
          </div>
          <div className="pt-5">
            <label
              htmlFor={`caption`}
              className="inline-block mb-2 text-base font-medium"
            >
              Caption
            </label>
            <input
              type="text"
              id="caption"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Caption"
              name="caption"
              onChange={validation.handleChange}
              value={validation.values.caption}
            />
            {validation.errors.caption && validation.touched.caption && (
              <p className="mt-1 text-sm text-red-400">
                {String(validation.errors.caption)}
              </p>
            )}
          </div>
        </form>
      ) : (
        <div className="w-full">
          {/* <h6 className="text-15 leading-[1] capitalize">{subSection.type}</h6> */}
          {subSection.title && (
            <p className="pt-3 text-lg">{subSection.title}</p>
          )}
          <div className="h-[250px] flex justify-center w-auto">
            <iframe
              className="rounded-md h-full w-auto aspect-video"
              title="Embedded YouTube Video"
              src={subSection.attrs.url.replace("shorts/", "embed/")}
            ></iframe>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-x-2.5 pt-5">
        {subSection.isEditing ? (
          <div className="flex gap-x-3">
            {isCancel && (
              <button
                type="button"
                className={`${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } text-red-500`}
                onClick={() => handleCancelSubSection()}
                disabled={isLoading}
              >
                <div className="flex align-center">
                  <span className="ml-1">Cancel</span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`text-green-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-green-600 hover:text-white"
              } bg-green-100 btn focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20`}
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <Save className="size-4"></Save>
                <span className="ml-1">Save</span>
              </div>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`text-custom-500 ${
              isLoading
                ? "cursor-not-allowed opacity-50"
                : " hover:bg-custom-600 hover:text-white"
            } btn bg-custom-100 ocus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
            onClick={() => {
              handleEditSubSection(sectionOrder, subSection);
              setIsCancel(true);
            }} // Switch to edit mode when clicked
            disabled={isLoading}
          >
            <div className="flex align-center">
              <FileEdit className="size-4"></FileEdit>
              <span className="ml-1">Edit</span>
            </div>
          </button>
        )}
        <button
          type="button"
          className={`${
            isLoading ? "cursor-not-allowed opacity-50" : ""
          } text-red-500`}
          onClick={() => setDeleteModal(true)}
          disabled={isLoading}
        >
          <div className="flex align-center">
            <Trash2 className="size-4 text-red-500"></Trash2>
            <span className="ml-1">Delete</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Embed;
